import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import "./AgentHotelOwnerProfile.css";
import { useNavigate } from "react-router-dom";
import NavUser from "../components/NavUser";
import {
  FaEdit,
  FaHotel,
  FaCalendarCheck,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaCoins,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaPlus,
  FaArrowRight,
  FaUser,
  FaInfoCircle,
  FaCopy,
  FaTrashAlt,
  FaStar,
} from "react-icons/fa";
import hotel1 from "../assets/hotel1.png";
import { getFileUrl } from "../utils/imageUrl";
import { getApiBaseUrl, getBackendBaseUrl } from "../utils/apiConfig";

export default function AgentHotelOwnerProfile() {
  const { userId, agentDetailsId } = useParams();

  const [hotel, setHotel] = useState(null);
  const [hotelOwner, setHotelOwner] = useState(null);
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [totalPayout, setTotalPayout] = useState(null);
  const [lastPayout, setLastPayout] = useState(null);
  const [coinBalance, setCoinBalance] = useState(null);
  const [agentMonthlyEarnings, setAgentMonthlyEarnings] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const today = new Date().toISOString().split("T")[0];


  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    async function loadData() {
      if (!userId || !agentDetailsId) return;

      try {
        const hotelRes = await axios.get(
          `${BACKEND_URL}/hotel-owner/owner/${userId}/hotels`
        );
        const hotelData = hotelRes.data.hotels?.[0];
        setHotel(hotelData);

        const ownerRes = await axios.get(
          `${BACKEND_URL}/hotel-owner/owner/${userId}`
        );
        setHotelOwner(ownerRes.data?.hotelOwner);
      } catch (e) {
        console.error("ERROR LOADING CRITICAL PROFILE DATA:", e);
        return; // Stop if we can't even load the hotel
      }

      try {
        const listingsRes = await axios.get(
          `${BACKEND_URL}/hotel-owner/owner/${userId}/listings`
        );
        setListings(listingsRes.data?.listings || []);
      } catch (e) { console.error("Error loading listings", e); }

      try {
        const bookingsRes = await axios.get(
          `${BACKEND_URL}/hotel-owner/owner/${userId}/${agentDetailsId}/bookings`
        );
        setBookings(bookingsRes.data?.bookings || []);
      } catch (e) { console.error("Error loading bookings", e); }

      try {
        const payoutRes = await axios.get(
          `${BACKEND_URL}/hotel-owner/owner/${userId}/total-payout`
        );
        setTotalPayout(payoutRes.data?.totalPayout);
      } catch (e) { console.error("Error loading total payout", e); }

      try {
        const lastPayoutRes = await axios.get(
          `${BACKEND_URL}/hotel-owner/owner/${userId}/last-payout`
        );
        setLastPayout(lastPayoutRes.data?.lastPayout);
      } catch (e) { console.error("Error loading last payout", e); }

      try {
        const coinsRes = await axios.get(
          `${BACKEND_URL}/coin-transactions/balance/${userId}`
        );
        setCoinBalance(coinsRes.data?.coin_balance);
      } catch (e) { console.error("Error loading coin balance", e); }

      try {
        const earningsRes = await axios.get(
          `${BACKEND_URL}/agents/earnings/${agentDetailsId}`
        );
        setAgentMonthlyEarnings(earningsRes.data?.earnings);
      } catch (e) { console.error("Error loading earnings", e); }

      try {
        const reviewsRes = await axios.get(
          `${BACKEND_URL}/hotel-owner/owner/${userId}/reviews`
        );
        setReviews(reviewsRes.data?.reviews || []);
      } catch (e) { console.error("Error loading reviews", e); }
    }

    loadData();
  }, [userId, agentDetailsId, BACKEND_URL]);

  const handleCopyLink = async (token) => {
    if (!token) {
      alert("No public link available for this listing.");
      return;
    }

    const fullUrl = `${window.location.origin}/listing/${token}`;

    try {
      await navigator.clipboard.writeText(fullUrl);
      alert("Listing link copied:\n" + fullUrl);
    } catch (err) {
      console.error("Copy failed:", err);
      alert("Failed to copy link");
    }
  };

  const handleDeleteListing = async (listingId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this listing? This action cannot be undone."
    );

    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("jwt_token");
      const API_URL = getApiBaseUrl();
      const response = await fetch(`${API_URL}/hotels/hotel-listing/${listingId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to delete listing");
      }

      // Remove listing from UI
      setListings((prev) => prev.filter((l) => l.id !== listingId));

      alert("Listing deleted successfully");
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete listing: " + err.message);
    }
  };

  const todaysBookings = bookings.filter(
    (b) => b.checking_date === today
  );

  if (!hotel) {
    return (
      <div className="agent-loading-container">
        <div className="agent-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="agent-profile-dashboard">
      <NavUser />
      
      <div className="agent-dashboard-container">
        <div className="agent-dashboard-grid">
          
          {/* SIDEBAR */}
          <aside className="agent-dashboard-sidebar">
            
            {/* PROFILE CARD */}
            <div className="agent-sidebar-card">
              <div className="agent-avatar-container">
                {hotel.logo ? (
                  <img
                    src={getFileUrl(hotel.logo, BACKEND_URL)}
                    alt={hotel.name}
                    className="agent-avatar-img"
                  />
                ) : (
                  <div className="agent-avatar-placeholder" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0f6eb5', color: '#fff', fontSize: '2.5rem', fontWeight: 'bold' }}>
                    {hotel.name?.charAt(0) || "H"}
                  </div>
                )}
              </div>
              
              <div className="agent-profile-info">
                <h2 className="agent-hotel-name">{hotel.name}</h2>
                
                <div className="agent-hotel-meta">
                  <div className="agent-meta-item">
                    <FaMapMarkerAlt className="agent-meta-icon" />
                    <span>{hotel.city}{hotelOwner?.Country?.name ? ` • ${hotelOwner.Country.name}` : ""}</span>
                  </div>
                  {hotelOwner?.phone_number && (
                    <div className="agent-meta-item">
                      <FaPhone className="agent-meta-icon" />
                      <span>{hotelOwner.phone_number}</span>
                    </div>
                  )}
                </div>
                
                <button
                  className="agent-edit-profile-btn"
                  onClick={() =>
                    navigate(`/edit-profile`, { state: { hotelOwner, hotel } })
                  }
                >
                  <FaEdit /> Edit Profile
                </button>
              </div>
            </div>

            {/* PAYOUT SUMMARY */}
            <div className="agent-payout-card">
              <h3 className="agent-payout-title">
                <FaMoneyBillWave /> Payout Summary
              </h3>
              <div className="agent-payout-metrics">
                <div className="agent-payout-row">
                  <span className="agent-payout-label">Total Payout</span>
                  <span className="agent-payout-value highlight">${totalPayout || 0}</span>
                </div>
                <div className="agent-payout-row">
                  <span className="agent-payout-label">Last Payout</span>
                  <span className="agent-payout-value">${lastPayout || 0}</span>
                </div>
              </div>
            </div>

          </aside>

          {/* MAIN CONTENT AREA */}
          <main className="agent-dashboard-main">
            
            {/* QUICK STATS */}
            <div className="agent-main-section">
              <div className="agent-section-header">
                <h3 className="agent-section-title">
                  <FaInfoCircle className="agent-section-title-icon" /> Quick Stats Summary
                </h3>
              </div>
              
              <div className="agent-stats-grid">
                <div className="agent-stat-card">
                  <div className="agent-stat-icon-wrapper">
                    <FaHotel />
                  </div>
                  <div className="agent-stat-details">
                    <span className="agent-stat-label">Active Listings</span>
                    <p className="agent-stat-value">{listings.length}</p>
                  </div>
                </div>
                
                <div className="agent-stat-card">
                  <div className="agent-stat-icon-wrapper">
                    <FaCalendarCheck />
                  </div>
                  <div className="agent-stat-details">
                    <span className="agent-stat-label">Total Bookings</span>
                    <p className="agent-stat-value">{bookings.length}</p>
                  </div>
                </div>
                
                <div className="agent-stat-card">
                  <div className="agent-stat-icon-wrapper">
                    <FaMoneyBillWave />
                  </div>
                  <div className="agent-stat-details">
                    <span className="agent-stat-label">Monthly Earnings</span>
                    <p className="agent-stat-value">${agentMonthlyEarnings || 0}</p>
                  </div>
                </div>
                
                <div className="agent-stat-card">
                  <div className="agent-stat-icon-wrapper">
                    <FaCoins />
                  </div>
                  <div className="agent-stat-details">
                    <span className="agent-stat-label">Guide Coins</span>
                    <p className="agent-stat-value">{coinBalance || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* TODAY'S BOOKINGS */}
            <div className="agent-main-section">
              <div className="agent-section-header">
                <h3 className="agent-section-title">
                  <FaCalendarAlt className="agent-section-title-icon" /> Today's Bookings
                </h3>
              </div>
              
              <div className="agent-booking-list">
                {todaysBookings.length === 0 ? (
                  <div className="agent-empty-state">
                    <div className="agent-empty-icon">
                      <FaCalendarAlt />
                    </div>
                    <h4 className="agent-empty-title">No Bookings Scheduled</h4>
                    <p className="agent-empty-desc">There are no check-ins scheduled for today.</p>
                  </div>
                ) : (
                  todaysBookings.map((b, i) => (
                    <div key={i} className="agent-booking-item">
                      <div>
                        <h4 className="agent-card-title">{b.listing?.name}</h4>
                        <div className="agent-card-details">
                          <div className="agent-card-row">
                            <FaUser className="agent-card-icon" />
                            <span><strong>Guest:</strong> {b.user?.full_name}</span>
                          </div>
                          <div className="agent-card-row">
                            <FaEnvelope className="agent-card-icon" />
                            <span>{b.user?.email}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        className="agent-card-btn"
                        onClick={() => {
                          setSelectedBooking(b);
                          setShowBookingModal(true);
                        }}
                      >
                        <FaInfoCircle /> View Details
                      </button>
                    </div>
                  ))
                )}
              </div>

              <button
                className="agent-view-all-btn"
                onClick={() =>
                  navigate(`/agent/hotel-owner/${userId}/${agentDetailsId}/bookings`)
                }
              >
                View All Bookings <FaArrowRight />
              </button>
            </div>

            {/* ROOM LISTINGS */}
            <div className="agent-main-section">
              <div className="agent-section-header">
                <h3 className="agent-section-title">
                  <FaHotel className="agent-section-title-icon" /> Room Listings Overview
                </h3>
                <button
                  className="agent-primary-btn"
                  onClick={() =>
                    navigate(`/hotel/${hotelOwner?.id}`)
                  }
                >
                  <FaPlus /> Add New Listing
                </button>
              </div>
              
              <div className="hotel-summary-grid">
                {listings.length === 0 ? (
                  <div className="agent-empty-state">
                    <div className="agent-empty-icon">
                      <FaHotel />
                    </div>
                    <h4 className="agent-empty-title">No Listings Created</h4>
                    <p className="agent-empty-desc">Get started by creating your first hotel listing.</p>
                  </div>
                ) : (
                  listings.map((hotel) => (
                    <div key={hotel.id} className="hotel-summary-item">
                      <img
                        src={
                          hotel.hotel_images && hotel.hotel_images.length > 0
                            ? `${getBackendBaseUrl()}${hotel.hotel_images[0].url}`
                            : hotel1
                        }
                        alt={hotel.name || "Hotel"}
                        className="hotel-summary-img"
                      />

                      <div className="hotel-summary-info">
                        <h4 className="hotel-summary-name">{hotel.name}</h4>

                        <p className="hotel-summary-description">
                          {hotel.description || "No description available"}
                        </p>

                        <p className="hotel-summary-price">
                          <strong>Price:</strong> Rs. {hotel.price}
                        </p>

                        <p className="hotel-summary-meta">
                          <strong>Room Type:</strong> {hotel.room_type?.name || "N/A"}
                        </p>

                        <p className="hotel-summary-meta">
                          <strong>Discounts:</strong> {hotel.discounts || "N/A"} %
                        </p>
                        <p className="hotel-summary-meta">
                          <strong>Coin Rewards:</strong> {hotel.coin_rewards || "N/A"}
                        </p>
                        <p className="hotel-summary-meta">
                          <strong>Max Guests:</strong> {hotel.max_guest || "N/A"}
                        </p>

                        <p className="hotel-summary-meta">
                          <strong>Status:</strong>{" "}
                          <span className={`status-badge ${hotel.listing_status?.name?.toLowerCase() || "unknown"}`}>
                            {hotel.listing_status?.name || "Unknown"}
                          </span>
                        </p>

                        <div className="listing-action-row">
                          <button
                            className="copy-link-btn"
                            onClick={() => handleCopyLink(hotel.public_link)}
                            title="Copy Public Link"
                          >
                            <FaCopy size={16} />
                          </button>

                          <button
                            className="edit-listing-btn"
                            onClick={() => navigate(`/update-hotel/${hotel.id}`)}
                            title="Edit Listing"
                          >
                            <FaEdit size={16} />
                          </button>

                          <button
                            className="delete-listing-btn"
                            onClick={() => handleDeleteListing(hotel.id)}
                            title="Delete Listing"
                          >
                            <FaTrashAlt size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* TRAVELER REVIEWS */}
            <div className="agent-main-section">
              <div className="agent-section-header">
                <h3 className="agent-section-title">
                  <FaStar className="agent-section-title-icon" style={{ color: "#f59e0b" }} /> Guest Reviews
                </h3>
              </div>
              
              <div className="agent-reviews-list">
                {reviews.length > 0 ? (
                  reviews.map((review) => (
                    <div key={review.id} className="agent-review-card">
                      <div className="agent-review-header">
                        <div className="agent-review-user-info">
                          <div className="agent-review-avatar-placeholder">
                            {review.user?.full_name?.charAt(0) || "G"}
                          </div>
                          <div>
                            <strong className="agent-review-author">{review.user?.full_name}</strong>
                            {review.listing?.title && (
                              <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                                {review.listing.title}
                              </div>
                            )}
                          </div>
                        </div>
                        {review.rating && (
                          <div className="agent-review-rating">
                            {Array.from({ length: Math.round(Number(review.rating) || 5) }).map((_, idx) => (
                              <FaStar key={idx} />
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="agent-review-comment">{review.comment}</p>
                    </div>
                  ))
                ) : (
                  <div className="agent-empty-state" style={{ border: "none", padding: "2rem" }}>
                    <div className="agent-empty-icon">
                      <FaStar />
                    </div>
                    <h4 className="agent-empty-title">No Reviews Yet</h4>
                    <p className="agent-empty-desc">This hotel hasn't received any guest reviews yet.</p>
                  </div>
                )}
              </div>
            </div>

          </main>
        </div>
      </div>

      {/* BOOKING MODAL */}
      {showBookingModal && selectedBooking && (
        <div className="agent-modal-overlay" onClick={() => setShowBookingModal(false)}>
          <div className="agent-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="agent-modal-header">
              <h3>Booking Details</h3>
            </div>
            <div className="agent-modal-body">
              <h4 className="agent-modal-section-title">Room & Pricing</h4>
              <div className="agent-detail-grid">
                <div className="agent-detail-item full-width">
                  <span className="agent-detail-label">Selected Room</span>
                  <span className="agent-detail-value">{selectedBooking.listing?.name}</span>
                </div>
                <div className="agent-detail-item">
                  <span className="agent-detail-label">Total Price</span>
                  <span className="agent-detail-value" style={{ color: "#0f6eb5" }}>
                    Rs. {selectedBooking.price}
                  </span>
                </div>
                <div className="agent-detail-item">
                  <span className="agent-detail-label">Number of Guests</span>
                  <span className="agent-detail-value">{selectedBooking.number_of_guests}</span>
                </div>
              </div>

              <h4 className="agent-modal-section-title">Guest Information</h4>
              <div className="agent-detail-grid">
                <div className="agent-detail-item full-width">
                  <span className="agent-detail-label">Full Name</span>
                  <span className="agent-detail-value">{selectedBooking.user?.full_name}</span>
                </div>
                <div className="agent-detail-item">
                  <span className="agent-detail-label">Email Address</span>
                  <span className="agent-detail-value" style={{ fontSize: "0.85rem", wordBreak: "break-all" }}>
                    {selectedBooking.user?.email}
                  </span>
                </div>
                <div className="agent-detail-item">
                  <span className="agent-detail-label">Phone Number</span>
                  <span className="agent-detail-value">{selectedBooking.user?.phone_number || "N/A"}</span>
                </div>
              </div>

              <h4 className="agent-modal-section-title">Dates</h4>
              <div className="agent-detail-grid">
                <div className="agent-detail-item">
                  <span className="agent-detail-label">Check-In Date</span>
                  <span className="agent-detail-value" style={{ color: "#10b981" }}>
                    {selectedBooking.checking_date}
                  </span>
                </div>
                <div className="agent-detail-item">
                  <span className="agent-detail-label">Check-Out Date</span>
                  <span className="agent-detail-value" style={{ color: "#ef4444" }}>
                    {selectedBooking.checkout_date}
                  </span>
                </div>
              </div>
            </div>
            <div className="agent-modal-footer">
              <button
                className="agent-modal-close-btn"
                onClick={() => setShowBookingModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
